import { useEffect, useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { X, AlertTriangle, Check, Users, Clock, CalendarRange, BellRing } from 'lucide-react';
import { usePreviewVariableFill, useApplyVariableFill } from '@/api/resources';
import { Button } from '@/components/ui/Button';
import type { VariableFillPlan, VariableFillEmployeePlan, VariableStaffMember } from '@/types';

/**
 * « Voulez-vous recalculer les horaires du personnel variable ? »
 *
 * S'ouvre après la validation d'une absence, ou depuis le planning. Montre un
 * APERÇU (rien n'est écrit) : qui viendrait, quand, combien d'heures ça ajoute,
 * et ce qui reste non couvert. Le titulaire coche qui il veut recalculer.
 *
 * À l'application, les salariés dont le planning bouge reçoivent une
 * notification à accuser réception depuis l'app mobile.
 */
export default function VariableFillModal({
  from,
  to,
  staff,
  title = 'Recalculer les horaires du personnel variable ?',
  reason,
  onClose,
}: {
  /** Période à recalculer, au format "YYYY-MM-DD". */
  from: string;
  to: string;
  /** Les salariés en horaires variables (liste à cocher). */
  staff: VariableStaffMember[];
  title?: string;
  /** Contexte déclencheur, ex. « Absence de Zine validée du 3 au 10 mars ». */
  reason?: string;
  onClose: () => void;
}) {
  // Par défaut tout le monde est coché : le cas courant est « recalcule tout ».
  const [selected, setSelected] = useState<string[]>(staff.map((s) => s.id));
  const [plan, setPlan] = useState<VariableFillPlan | null>(null);
  const [done, setDone] = useState<{ notified: number; written: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const preview = usePreviewVariableFill();
  const apply = useApplyVariableFill();

  // Un poste non pourvu (aucun pharmacien sur le créneau, par exemple) n'a rien
  // à faire replié au fond de la liste : c'est le manque le plus grave, et
  // aucun renfort disponible ne sait le combler. On le remonte en évidence.
  const positionGaps = useMemo(
    () => plan?.gapsRemaining.filter((g) => g.requiredPosition) ?? [],
    [plan]
  );
  const missionGaps = useMemo(
    () => plan?.gapsRemaining.filter((g) => !g.requiredPosition) ?? [],
    [plan]
  );

  // Aperçu recalculé à chaque changement de sélection : le titulaire voit
  // immédiatement l'effet de cocher / décocher quelqu'un.
  useEffect(() => {
    if (selected.length === 0) {
      setPlan(null);
      return;
    }
    let cancelled = false;
    setError(null);
    preview
      .mutateAsync({ from, to, employeeIds: selected })
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(e.message);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, selected.join(',')]);

  const toggle = (id: string) =>
    setSelected((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  const withHours = useMemo(
    () => (plan?.employees ?? []).filter((e) => e.slots.length > 0),
    [plan]
  );
  const outsideTotal = withHours.reduce((s, e) => s + e.outsideDeclaredCount, 0);

  // Dépassement du contrat : signal, jamais un blocage. Sur un horaire variable
  // c'est normal — le titulaire veut juste être prévenu quand « ça fait beaucoup ».
  const overContractPeople = withHours.filter((e) => e.overContractWeeks.length > 0);

  async function submit() {
    setError(null);
    try {
      const res = await apply.mutateAsync({ from, to, employeeIds: selected });
      setDone({ notified: res.notified, written: res.result.written });
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const period =
    from === to
      ? format(parseISO(from), 'd MMMM', { locale: fr })
      : `du ${format(parseISO(from), 'd MMM', { locale: fr })} au ${format(parseISO(to), 'd MMM yyyy', { locale: fr })}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6 relative max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"
        >
          <X size={16} />
        </button>

        {/* ---------------------------------------------------------------- */}
        {/* Écran de confirmation après application                          */}
        {/* ---------------------------------------------------------------- */}
        {done ? (
          <div className="text-center py-6">
            <div className="w-12 h-12 rounded-full bg-primary-bg flex items-center justify-center mx-auto mb-3">
              <Check size={22} className="text-primary" />
            </div>
            <div className="text-[15px] font-semibold text-slate-900">Horaires recalculés</div>
            <div className="text-[13px] text-slate-600 mt-1.5">
              {done.written} journée{done.written > 1 ? 's' : ''} mise
              {done.written > 1 ? 's' : ''} à jour.
            </div>
            {done.notified > 0 ? (
              <div className="text-[12.5px] text-slate-600 mt-3 flex items-center justify-center gap-1.5">
                <BellRing size={13} className="text-primary" />
                {done.notified} salarié{done.notified > 1 ? 's' : ''} notifié
                {done.notified > 1 ? 's' : ''} — accusé de réception attendu sur mobile.
              </div>
            ) : (
              <div className="text-[12.5px] text-slate-500 mt-3">
                Aucun changement réel : personne n'a été notifié.
              </div>
            )}
            <div className="mt-5">
              <Button variant="primary" onClick={onClose}>
                Fermer
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-[15px] font-semibold text-slate-900 pr-8">{title}</div>
            {reason && <div className="text-[12.5px] text-slate-500 mt-1">{reason}</div>}
            <div className="text-[12.5px] text-slate-600 mt-2 flex items-center gap-1.5">
              <CalendarRange size={13} className="text-slate-400" />
              Période recalculée : <strong className="text-slate-800">{period}</strong>
            </div>

            {/* Liste à cocher */}
            <div className="mt-4">
              <div className="label mb-2 flex items-center gap-1.5">
                <Users size={13} /> Qui recalculer ?
              </div>
              <div className="space-y-1.5">
                {staff.map((s) => {
                  const p = plan?.employees.find((e) => e.employeeId === s.id);
                  const checked = selected.includes(s.id);
                  return (
                    <label
                      key={s.id}
                      className={`flex items-center gap-3 p-2.5 rounded-xl border cursor-pointer transition-colors ${
                        checked ? 'border-primary/40 bg-primary-bg/30' : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                    >
                      <input type="checkbox" checked={checked} onChange={() => toggle(s.id)} className="rounded" />
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: s.color }}
                      />
                      <span className="text-[13px] font-medium text-slate-900 flex-1">
                        {s.firstName} {s.lastName}
                        {s.isOwner && (
                          <span className="ml-2 text-[10.5px] font-normal text-slate-500">titulaire</span>
                        )}
                      </span>
                      {checked && p && (
                        <span className="text-[12px] text-slate-600 tabular-nums whitespace-nowrap">
                          {p.slots.length === 0 ? (
                            <span className="text-slate-400">rien à combler</span>
                          ) : (
                            <>
                              +{p.addedHours} h · {p.daysCount} j
                            </>
                          )}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Aperçu */}
            <div className="mt-4">
              {preview.isPending && !plan && (
                <div className="text-[12.5px] text-slate-500 py-3">Calcul de l'aperçu…</div>
              )}

              {selected.length === 0 && (
                <div className="text-[12.5px] text-slate-500 py-3">
                  Sélectionnez au moins un salarié à recalculer.
                </div>
              )}

              {plan && selected.length > 0 && (
                <>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <Stat label="Trous comblés" value={`${plan.gapsClosed}`} />
                    <Stat label="Heures ajoutées" value={`${plan.totalAddedHours} h`} />
                    <Stat
                      label="Non couverts"
                      value={`${plan.gapsRemaining.length}`}
                      warn={plan.gapsRemaining.length > 0}
                    />
                  </div>

                  {positionGaps.length > 0 && (
                    <div className="bg-danger-bg border border-danger/30 rounded-lg p-3 text-[12px] text-slate-700 flex items-start gap-2 mb-3">
                      <AlertTriangle size={14} className="text-danger mt-0.5 flex-shrink-0" />
                      <div>
                        <strong>Poste non pourvu — aucun renfort ne peut le prendre.</strong>{' '}
                        Il manque quelqu'un du bon poste sur ces créneaux ; personne en horaires
                        variables n'est disponible pour l'assurer.
                        <ul className="mt-1.5 space-y-0.5">
                          {positionGaps.slice(0, 8).map((g, i) => (
                            <li key={i} className="tabular-nums">
                              {format(parseISO(g.date), 'EEE d MMM', { locale: fr })} · {g.from}–{g.to}{' '}
                              — <strong>{g.missionLabel}</strong>
                              {g.missing > 1 ? ` (manque ${g.missing})` : ''}
                            </li>
                          ))}
                          {positionGaps.length > 8 && (
                            <li className="text-slate-500">…et {positionGaps.length - 8} autre(s).</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  )}

                  {outsideTotal > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[12px] text-slate-700 flex items-start gap-2 mb-3">
                      <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong>{outsideTotal} créneau{outsideTotal > 1 ? 'x' : ''}</strong> posé
                        {outsideTotal > 1 ? 's' : ''} en dehors des disponibilités déclarées — personne
                        d'autre n'était disponible sur ces manques.
                      </div>
                    </div>
                  )}

                  {overContractPeople.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[12px] text-slate-700 flex items-start gap-2 mb-3">
                      <AlertTriangle size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                      <div>
                        <strong>Au-delà des heures contractuelles.</strong> Rien ne bloque — c'est
                        normal sur un horaire variable — mais autant que vous le sachiez :
                        <ul className="mt-1.5 space-y-0.5">
                          {overContractPeople.map((e) => (
                            <li key={e.employeeId} className="tabular-nums">
                              <strong>
                                {e.firstName} {e.lastName}
                              </strong>{' '}
                              — {e.overContractWeeks.length} semaine
                              {e.overContractWeeks.length > 1 ? 's' : ''} au-dessus de{' '}
                              {e.contractHours} h, jusqu'à <strong>{e.peakWeeklyHours} h</strong>
                              <span className="text-slate-500">
                                {' '}
                                (+
                                {Math.max(...e.overContractWeeks.map((w) => w.over))} h max)
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {withHours.length === 0 ? (
                    <div className="text-[12.5px] text-slate-500 py-2">
                      Aucun trou à combler sur cette période : rien ne changera.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {withHours.map((e) => (
                        <EmployeeSlots key={e.employeeId} plan={e} />
                      ))}
                    </div>
                  )}

                  {missionGaps.length > 0 && (
                    <details className="mt-3">
                      <summary className="text-[12px] text-slate-500 cursor-pointer hover:text-slate-700">
                        {missionGaps.length} manque{missionGaps.length > 1 ? 's' : ''} que
                        personne ne peut couvrir
                      </summary>
                      <ul className="mt-2 space-y-1">
                        {missionGaps.slice(0, 12).map((g, i) => (
                          <li key={i} className="text-[11.5px] text-slate-500 tabular-nums">
                            {format(parseISO(g.date), 'EEE d MMM', { locale: fr })} · {g.from}–{g.to} ·{' '}
                            {g.missionLabel} (manque {g.missing})
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </>
              )}
            </div>

            {error && (
              <div className="bg-danger-bg border border-danger/30 rounded-lg p-3 text-[12px] text-danger mt-3">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 mt-5">
              <Button variant="ghost" onClick={onClose}>
                Ne pas recalculer
              </Button>
              <Button
                variant="primary"
                icon={Check}
                loading={apply.isPending}
                disabled={selected.length === 0 || !plan}
                onClick={submit}
              >
                Appliquer et notifier
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className={`rounded-xl border p-2.5 ${warn ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50/60'}`}>
      <div className="text-[16px] font-semibold text-slate-900 tabular-nums">{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function EmployeeSlots({ plan }: { plan: VariableFillEmployeePlan }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: plan.color }} />
        <span className="text-[13px] font-semibold text-slate-900">
          {plan.firstName} {plan.lastName}
        </span>
        {plan.overContractWeeks.length > 0 && (
          <span
            className="text-[10.5px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap"
            title={plan.overContractWeeks
              .map((w) => `semaine du ${w.weekStart} : ${w.hours} h (+${w.over})`)
              .join('\n')}
          >
            jusqu'à {plan.peakWeeklyHours} h/sem. (contrat {plan.contractHours} h)
          </span>
        )}
        <span className="text-[12px] text-slate-500 flex items-center gap-1 ml-auto tabular-nums">
          <Clock size={12} /> +{plan.addedHours} h
        </span>
      </div>
      <ul className="space-y-1">
        {plan.slots.map((s, i) => (
          <li key={i} className="flex items-center gap-2 text-[11.5px] tabular-nums">
            <span className="text-slate-700 w-28 flex-shrink-0">
              {format(parseISO(s.date), 'EEE d MMM', { locale: fr })}
            </span>
            <span className="text-slate-900 font-medium w-24 flex-shrink-0">
              {s.start}–{s.end}
            </span>
            <span className="text-slate-500 flex-1">{s.missionLabel}</span>
            {!s.withinDeclared && (
              <span className="text-[10.5px] text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                hors dispo
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
